import type { Session } from '@/shared/types/session';
import type { LoginPlan } from '@/modules/auth/types';
import type { PermissionMap } from '@/shared/permissions';
import { STORAGE_KEYS } from '@/shared/constants/storage-keys';
import { localStorageUtil } from '@/shared/utils/local-storage';
import { setAuthToken } from '@/shared/utils/auth-token';
import { writeSessionCookie } from '@/shared/utils/cookies';
import { setStoredPlan } from '@/shared/utils/stored-plan';
import { setStoredAccess } from '@/shared/utils/stored-access';

/**
 * The accounts this browser is signed in to — the "add account / switch
 * account" book behind the topbar profile menu.
 *
 * ONE account is active at a time. Every tab shares the active one (it lives in
 * the same localStorage + cookie the app has always used), so switching in one
 * tab moves the others too on their next load. What this store adds is that
 * signing into a second account no longer throws the first one away: each
 * account keeps its own token, plan and permission set, and switching is a
 * local write plus a reload — no re-typing a password.
 *
 * Not to be confused with the WORKSPACE switcher (sidebar): that moves one
 * login between the tenants it holds memberships in, and asks the API for a
 * fresh token each time. This moves between DIFFERENT logins.
 *
 * Keyed by `userId` — one entry per login identity. A login that switches
 * workspace updates its entry in place rather than adding a second one, so the
 * two switchers never show the same account twice.
 *
 * SECURITY. Each entry holds that account's bearer token, so this is the same
 * exposure the app already accepts for the active token in `ps_auth_token`,
 * multiplied by the number of saved accounts: anything that can run script on
 * this origin can read them. Signing out of an account DROPS its entry (see
 * `forgetAccount`) rather than leaving a live token behind, and the list is
 * capped at {@link MAX_ACCOUNTS}.
 */

/** One signed-in account, with everything needed to make it active again. */
export interface SavedAccount {
    /** Exactly what `writeSessionCookie` takes / `readSessionCookie` returns. */
    session:     Session;
    /** That account's bearer token (JWT from its own sign-in). */
    token:       string;
    plan:        LoginPlan | null;
    /** Delegated permissions; null = owner (full access). */
    permissions: PermissionMap | null;
    /** Drives the switcher's order and which account sign-out falls back to. */
    lastUsedAt:  number;
}

/**
 * Most-recent 5. A cap keeps a shared machine from accumulating tokens for
 * accounts nobody uses any more; the least-recently-used entry falls off.
 */
const MAX_ACCOUNTS = 5;

/** Saved accounts, most recently used first. Malformed entries are dropped. */
export function readAccounts(): SavedAccount[] {
    const raw = localStorageUtil.get<SavedAccount[]>(STORAGE_KEYS.ACCOUNTS);
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((a) => a?.session?.userId && a?.token)
        .sort((a, b) => (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0));
}

/** The saved entry for one login, if this browser has it. */
export function findAccount(userId: string): SavedAccount | null {
    return readAccounts().find((a) => a.session.userId === userId) ?? null;
}

/**
 * Record (or refresh) an account. Called on every sign-in and workspace switch,
 * so the stored token / plan / permissions never lag behind the live session —
 * a stale entry would switch the user into a workspace they've left.
 */
export function rememberAccount(entry: Omit<SavedAccount, 'lastUsedAt'>): void {
    const others = readAccounts().filter((a) => a.session.userId !== entry.session.userId);
    const next = [{ ...entry, lastUsedAt: Date.now() }, ...others].slice(0, MAX_ACCOUNTS);
    localStorageUtil.set(STORAGE_KEYS.ACCOUNTS, next);
}

/** Drop one account — signing out of it, so its token must not linger. */
export function forgetAccount(userId: string): void {
    localStorageUtil.set(
        STORAGE_KEYS.ACCOUNTS,
        readAccounts().filter((a) => a.session.userId !== userId),
    );
}

/** Drop every account ("sign out of all accounts"). */
export function forgetAllAccounts(): void {
    localStorageUtil.remove(STORAGE_KEYS.ACCOUNTS);
}

/**
 * Make a saved account the active one, writing exactly what sign-in writes:
 * token, session (cookie + localStorage), plan and permissions.
 *
 * Storage only — the caller reloads. Every consumer of this does a full page
 * load rather than a soft navigation, for the same reason the workspace
 * switcher does: role, nav, plan and permissions all differ per account, and a
 * clean boot re-reads them instead of trying to invalidate a graph of
 * account-scoped caches.
 */
export function activateAccount(account: SavedAccount): void {
    setAuthToken(account.token);
    writeSessionCookie(account.session);
    setStoredPlan(account.plan);
    setStoredAccess({ permissions: account.permissions });
    rememberAccount(account);
}
